import React, { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation } from '@apollo/client'
import { getSet } from '../graphql/queries'
import { updateSetAdmin } from '../graphql/mutation'
import {
  Typography,
  Box,
  Dialog,
  Button,
  Grid,
  Card,
  CardMedia,
  CardContent,
  Link,
  Paper,
  Chip,
  Select,
  MenuItem,
  FormControl,
  IconButton,
  Alert,
  Snackbar,
  Divider,
} from '@mui/material'
import ProgressBar from '../components/circularProgress/circularProgress'
import SetIcon from '@mui/icons-material/CollectionsBookmark'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import EditIcon from '@mui/icons-material/Edit'
import CheckIcon from '@mui/icons-material/Check'
import CloseIcon from '@mui/icons-material/Close'
import ImageIcon from '@mui/icons-material/Image'
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary'
import PersonIcon from '@mui/icons-material/Person'

export default function SingleSetPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { loading, data, refetch } = useQuery(getSet, {
    variables: { setId: id },
  })

  const [updateSet, { loading: updating }] = useMutation(updateSetAdmin, {
    onCompleted: () => {
      setSnackbar({ open: true, message: 'Status updated successfully!', severity: 'success' })
      refetch()
    },
    onError: (error) => {
      setSnackbar({ open: true, message: `Error: ${error.message}`, severity: 'error' })
    }
  })

  const [openImage, setOpenImage] = useState(false)
  const [selectedImage, setSelectedImage] = useState(null)
  const [openVideo, setOpenVideo] = useState(false)
  const [selectedVideo, setSelectedVideo] = useState(null)
  const [visibleCount, setVisibleCount] = useState(6)
  const [editingStatus, setEditingStatus] = useState(false)
  const [selectedStatus, setSelectedStatus] = useState('')
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' })

  const statusOptions = ['starting', 'processing', 'succeeded', 'canceled', 'failed']
  
  const getStatusColor = (status) => {
    const colors = {
      starting: '#2196f3',
      processing: '#ff9800',
      succeeded: '#4caf50',
      canceled: '#9e9e9e',
      failed: '#f44336'
    }
    return colors[status] || '#9e9e9e'
  }

  const handleStatusUpdate = async () => {
    try {
      await updateSet({
        variables: {
          setId: id,
          status: selectedStatus
        }
      })
      setEditingStatus(false)
    } catch (error) {
      // Error handled by onError callback
    }
  }

  const startEditingStatus = (currentStatus) => {
    setSelectedStatus(currentStatus)
    setEditingStatus(true)
  }

  if (loading) return <ProgressBar />

  const setData = data?.getUserSet ?? null
  if (!setData) return <Typography>No Data Found</Typography>

  const isVideoSet = setData.type === 'VIDEO'
  const contentItems = isVideoSet ? setData.videos : setData.images

  const handleUserClick = (userId) => {
    navigate(`/users/${userId}`)
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: '#f8fafc',
        py: 4,
      }}
    >
      <Box
        sx={{
          maxWidth: '1400px',
          mx: 'auto',
          px: 3,
        }}
      >
        {/* Header */}
        <Paper
          elevation={0}
          sx={{
            p: 2,
            mb: 2,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            borderRadius: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <SetIcon fontSize="medium" />
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              Set Details
            </Typography>
          </Box>
        </Paper>

        <Grid container spacing={3}>
          {/* Set Information Card */}
          <Grid item xs={12}>
            <Card
              elevation={0}
              sx={{
                borderRadius: 3,
                border: '1px solid #e2e8f0',
                height: 'fit-content'
              }}
            >
              <CardContent sx={{ p: 4 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, color: '#1e293b' }}>
                  Set Information
                </Typography>
                
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {/* Name and Total Count Row */}
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                    <Box sx={{ flex: 1, minWidth: 200 }}>
                      <Typography variant="body2" sx={{ color: '#64748b', mb: 0.5, fontWeight: 500 }}>
                        Name
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b' }}>
                        {setData.name || '-'}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1, backgroundColor: '#f8fafc', borderRadius: 2 }}>
                      {isVideoSet ? (
                        <VideoLibraryIcon sx={{ color: '#8b5cf6', fontSize: 20 }} />
                      ) : (
                        <ImageIcon sx={{ color: '#06b6d4', fontSize: 20 }} />
                      )}
                      <Typography variant="body1" sx={{ fontWeight: 600, color: '#1e293b' }}>
                        {contentItems ? contentItems.length : 0} {isVideoSet ? 'Videos' : 'Images'}
                      </Typography>
                    </Box>
                  </Box>

                  <Divider />

                  {/* Info Grid */}
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: '1fr 1fr 1fr 1fr 1fr' }, gap: 3 }}>
                    <Box>
                      <Typography variant="body2" sx={{ color: '#64748b', mb: 1, fontWeight: 500 }}>
                        User
                      </Typography>
                      {setData.userId ? (
                        <Button
                          variant="text"
                          startIcon={<PersonIcon />}
                          onClick={() => handleUserClick(setData.userId)}
                          sx={{
                            p: 0,
                            justifyContent: 'flex-start',
                            textTransform: 'none',
                            fontWeight: 600,
                            color: '#3b82f6',
                            fontSize: '0.875rem',
                            '&:hover': {
                              backgroundColor: 'transparent',
                              color: '#1d4ed8'
                            }
                          }}
                        >
                          {setData.userId.substring(0, 12)}...
                        </Button>
                      ) : (
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#64748b' }}>
                          -
                        </Typography>
                      )}
                    </Box>

                    <Box>
                      <Typography variant="body2" sx={{ color: '#64748b', mb: 1, fontWeight: 500 }}>
                        Type
                      </Typography>
                      <Chip
                        icon={isVideoSet ? <VideoLibraryIcon /> : <ImageIcon />}
                        label={setData.type || 'Unknown'}
                        size="small"
                        sx={{
                          backgroundColor: setData.type === 'VIDEO' ? '#8b5cf6' : '#06b6d4',
                          color: 'white',
                          fontWeight: 500,
                          textTransform: 'capitalize'
                        }}
                      />
                    </Box>

                    <Box>
                      <Typography variant="body2" sx={{ color: '#64748b', mb: 1, fontWeight: 500 }}>
                        Model
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b' }}>
                        {setData?.model?.version ? setData.model.version.substring(0, 15) + '...' : '-'}
                      </Typography>
                    </Box>

                    {setData.status && (
                      <Box>
                        <Typography variant="body2" sx={{ color: '#64748b', mb: 1, fontWeight: 500 }}>
                          Status
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {editingStatus ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <FormControl size="small" sx={{ minWidth: 120 }}>
                                <Select
                                  value={selectedStatus}
                                  onChange={(e) => setSelectedStatus(e.target.value)}
                                  disabled={updating}
                                >
                                  {statusOptions.map((status) => (
                                    <MenuItem key={status} value={status}>
                                      <Chip
                                        label={status}
                                        size="small"
                                        sx={{
                                          backgroundColor: getStatusColor(status),
                                          color: 'white',
                                          fontWeight: 500,
                                          textTransform: 'capitalize'
                                        }}
                                      />
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                              <IconButton
                                onClick={handleStatusUpdate}
                                disabled={updating}
                                color="success"
                                size="small"
                              >
                                <CheckIcon />
                              </IconButton>
                              <IconButton
                                onClick={() => setEditingStatus(false)}
                                disabled={updating}
                                color="error"
                                size="small"
                              >
                                <CloseIcon />
                              </IconButton>
                            </Box>
                          ) : (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Chip
                                label={setData.status}
                                size="small"
                                sx={{
                                  backgroundColor: getStatusColor(setData.status),
                                  color: 'white',
                                  fontWeight: 500,
                                  textTransform: 'capitalize'
                                }}
                              />
                              <IconButton
                                onClick={() => startEditingStatus(setData.status)}
                                size="small"
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          )}
                        </Box>
                      </Box>
                    )}

                    <Box>
                      <Typography variant="body2" sx={{ color: '#64748b', mb: 1, fontWeight: 500 }}>
                        Created At
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b' }}>
                        {setData.createdAt ? new Date(setData.createdAt).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        }) : '-'}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Content Gallery Card */}
        <Grid item xs={12}>
          <Card
            elevation={0}
            sx={{
              borderRadius: 3,
              border: '1px solid #e2e8f0',
              mt: 2
            }}
          >
            <CardContent sx={{ p: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {isVideoSet ? (
                    <VideoLibraryIcon sx={{ color: '#64748b' }} />
                  ) : (
                    <ImageIcon sx={{ color: '#64748b' }} />
                  )}
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b' }}>
                    {isVideoSet ? 'Video Collection' : 'Image Collection'}
                  </Typography>
                </Box>
                <Chip
                  label={`${contentItems ? contentItems.length : 0} items`}
                  sx={{
                    backgroundColor: '#f1f5f9',
                    color: '#64748b',
                    fontWeight: 500
                  }}
                />
              </Box>

              {contentItems && contentItems.length > 0 ? (
                <>
                  <Grid container spacing={3}>
                    {contentItems.slice(0, visibleCount).map((item, index) => (
                      <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
                        <Card
                          elevation={0}
                          sx={{
                            borderRadius: 2,
                            border: '1px solid #e2e8f0',
                            overflow: 'hidden',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease-in-out',
                            '&:hover': {
                              transform: 'translateY(-4px)',
                              boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
                              borderColor: '#c7d2fe'
                            },
                          }}
                          onClick={() => {
                            if (isVideoSet) {
                              setSelectedVideo(item)
                              setOpenVideo(true)
                            } else {
                              setSelectedImage(item.url)
                              setOpenImage(true)
                            }
                          }}
                        >
                          <Box sx={{ position: 'relative', overflow: 'hidden' }}>
                            <CardMedia
                              component="div"
                              sx={{
                                pt: '75%',
                                backgroundImage: `url(${isVideoSet ? item.thumbnailUrl : item.url})`,
                                backgroundSize: 'cover',
                                backgroundPosition: 'center',
                                transition: 'transform 0.3s ease-in-out',
                                '&:hover': {
                                  transform: 'scale(1.05)'
                                }
                              }}
                            />
                            {isVideoSet && (
                              <Box
                                sx={{
                                  position: 'absolute',
                                  top: '50%',
                                  left: '50%',
                                  transform: 'translate(-50%, -50%)',
                                  backgroundColor: 'rgba(0, 0, 0, 0.8)',
                                  borderRadius: '50%',
                                  width: 56,
                                  height: 56,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  transition: 'all 0.2s ease-in-out',
                                  '&:hover': {
                                    backgroundColor: 'rgba(0, 0, 0, 0.9)',
                                    transform: 'translate(-50%, -50%) scale(1.1)'
                                  }
                                }}
                              >
                                <PlayArrowIcon sx={{ color: 'white', fontSize: 28, ml: 0.5 }} />
                              </Box>
                            )}
                            <Box
                              sx={{
                                position: 'absolute',
                                top: 8,
                                right: 8,
                                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                borderRadius: 1,
                                px: 1,
                                py: 0.5
                              }}
                            >
                              <Typography variant="caption" sx={{ fontWeight: 600, color: '#64748b' }}>
                                #{index + 1}
                              </Typography>
                            </Box>
                          </Box>

                          <CardContent sx={{ p: 2 }}>
                            <Typography
                              variant="body2"
                              sx={{ 
                                fontWeight: 600, 
                                color: '#1e293b',
                                textAlign: 'center',
                                mb: 1
                              }}
                            >
                              {isVideoSet ? `Video ${index + 1}` : `Image ${index + 1}`}
                            </Typography>
                            {isVideoSet && item.prompt && (
                              <Typography
                                variant="caption"
                                sx={{ 
                                  color: '#64748b',
                                  display: 'block',
                                  textAlign: 'center',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  lineHeight: 1.4
                                }}
                                title={item.prompt}
                              >
                                {item.prompt}
                              </Typography>
                            )}
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>

                  {contentItems.length > visibleCount && (
                    <Box sx={{ mt: 4, textAlign: 'center' }}>
                      <Button
                        onClick={() => setVisibleCount((prev) => prev + 6)}
                        variant="outlined"
                        sx={{
                          borderRadius: 2,
                          textTransform: 'none',
                          fontWeight: 500,
                          px: 4,
                          py: 1
                        }}
                      >
                        Show More ({contentItems.length - visibleCount} remaining)
                      </Button>
                    </Box>
                  )}
                </>
              ) : (
                <Box
                  sx={{
                    py: 8,
                    textAlign: 'center',
                    backgroundColor: '#f8fafc',
                    borderRadius: 2,
                    border: '2px dashed #cbd5e1'
                  }}
                >
                  {isVideoSet ? (
                    <VideoLibraryIcon sx={{ fontSize: 48, color: '#94a3b8', mb: 2 }} />
                  ) : (
                    <ImageIcon sx={{ fontSize: 48, color: '#94a3b8', mb: 2 }} />
                  )}
                  <Typography variant="h6" sx={{ color: '#64748b', mb: 1 }}>
                    No {isVideoSet ? 'videos' : 'images'} available
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#94a3b8' }}>
                    This set doesn't contain any {isVideoSet ? 'video' : 'image'} content yet.
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
        
        {/* Image Dialog */}
        <Dialog 
          open={openImage} 
          onClose={() => setOpenImage(false)} 
          maxWidth="lg"
          PaperProps={{
            sx: {
              borderRadius: 3,
              maxWidth: '90vw',
              maxHeight: '90vh'
            }
          }}
        >
          <Box sx={{ p: 2, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <img
              src={selectedImage}
              alt="Full Size Preview"
              style={{
                maxWidth: '100%',
                maxHeight: '80vh',
                objectFit: 'contain',
                borderRadius: 8
              }}
            />
          </Box>
        </Dialog>

        {/* Video Dialog */}
        <Dialog 
          open={openVideo} 
          onClose={() => setOpenVideo(false)} 
          maxWidth="lg" 
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              maxWidth: '90vw',
              maxHeight: '90vh'
            }
          }}
        >
          <Box sx={{ p: 3 }}>
            {selectedVideo && (
              <Box>
                <video
                  controls
                  style={{
                    width: '100%',
                    maxHeight: '70vh',
                    borderRadius: 8,
                    backgroundColor: '#000'
                  }}
                  poster={selectedVideo.thumbnailUrl}
                >
                  <source src={selectedVideo.url} type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
                {selectedVideo.prompt && (
                  <Box sx={{ mt: 3, p: 2, backgroundColor: '#f8fafc', borderRadius: 2 }}>
                    <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 500, mb: 1 }}>
                      Prompt:
                    </Typography>
                    <Typography variant="body1" sx={{ color: '#1e293b', lineHeight: 1.6 }}>
                      {selectedVideo.prompt}
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
          </Box>
        </Dialog>

        {/* Success/Error Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            sx={{ width: '100%', borderRadius: 2 }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </Box>
  )
}
